// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import DynamicDropdownPresentationPropsDefault from "@insite/mobius/DynamicDropdown/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import get from "@insite/mobius/utilities/get";
import { OptionProps } from "@vanmeter/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { fontConfig } from "@vanmeter/XDL/constants";
import CaretDown from "@vanmeter/XDL/Icons/IconCaretDown";
import { xBaseTheme } from "@vanmeter/XDL/types";
import { color, styledScrollbar } from "@vanmeter/XDL/Utilities";
import { css } from "styled-components";

export default {
    defaultProps: {
        ...DynamicDropdownPresentationPropsDefault,
        mergeCss: true,
        cssOverrides: {
            list: css`
                width: 100%;
                transform: translate(0, -4px);
                border: 1px solid ${color("common.ui4")};
                box-shadow: inset 0 1px 0 0 ${color("common.black", 0.1)};
                ${styledScrollbar({ width: 8, margined: 4 })}
            `,
            dropdownWrapper: css`
                && input[aria-expanded="true"] ~ ${IconWrapper} {
                    transform: rotate(180deg);
                    padding-left: 8px;
                }
                & input[disabled] ~ ${IconWrapper} {
                    color: ${color("common.ui3", 0.33)};
                }
            `,
            option: css`
                font-size: ${fontConfig.smallSingleLine.size}px;
                line-height: ${fontConfig.smallSingleLine.lineHeight};
                font-weight: ${fontConfig.weight.regular};
                letter-spacing: 0;
                color: ${color("text")};
                padding: 8px;
                &:not([disabled]):hover {
                    background: ${color("common.ui15")};
                    color: ${color("common.white")};
                }
                &[disabled],
                &[disabled] ~ ${IconWrapper} {
                    opacity: 0.33;
                }
                ${({ focused, disabled, selected, theme, noHover }: OptionProps) => {
                    // XNG-Changes: common.white
                    let background = get(theme, "colors.common.white");
                    let text = get(theme, "colors.text");
                    const hover = color("common.ui15");
                    const selectedBg = color("common.ui16");

                    if (disabled) {
                        background = get(theme, "colors.common.accent");
                        text = get(theme, "colors.text.disabled");
                    } else if (selected) {
                        // XNG-Changes: use selectedBg
                        background = selectedBg;
                        text = get(theme, "colors.primary.contrast");
                    } else if (focused) {
                        background = hover;
                    }
                    return css`
                        background: ${background};
                        color: ${text};
                        ${!disabled &&
                        !noHover &&
                        css`
                            &:hover {
                                background: ${hover};
                                // XNG-Changes
                                color: ${color("common.ui8")};
                            }
                        `}
                        ${disabled ? "cursor: not-allowed;" : "cursor: pointer;"}
                    `;
                }}
            `,
        },
        iconProps: {
            src: CaretDown,
            width: 9,
            height: 13,
            color: "common.ui5",
            css: css`
                &&& {
                    background-color: transparent;
                    display: flex;
                    justify-content: flex-end;
                    width: auto;
                    padding-right: 8px;
                }
            `,
        },
    },
} as xBaseTheme["dynamicDropdown"];
